import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MagnifyingGlass, Eye, X } from '@phosphor-icons/react';
import { getCustomers, getCustomer } from '../../utils/api';
import { formatPrice, formatDate } from '../../utils/helpers';
import { toast } from 'sonner';

const AdminCustomers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [loadingCustomer, setLoadingCustomer] = useState(false);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: 10,
      };
      if (search) params.search = search;
      
      const res = await getCustomers(params);
      setCustomers(res.data.data || []);
      setPagination(prev => ({
        ...prev,
        totalPages: res.data.pagination?.total_pages || 1,
        total: res.data.pagination?.total || 0
      }));
    } catch (error) {
      console.error('Error fetching customers:', error);
      toast.error('Failed to fetch customers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, [pagination.page, search]);

  const handleViewCustomer = async (customerId) => {
    setLoadingCustomer(true);
    setShowModal(true);
    try {
      const res = await getCustomer(customerId);
      setSelectedCustomer(res.data.data);
    } catch (error) {
      toast.error('Failed to fetch customer details');
      setShowModal(false);
    } finally {
      setLoadingCustomer(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="font-display text-2xl text-secondary">Customers</h1>
          <p className="font-body text-sm text-text-secondary">{pagination.total} total customers</p>
        </div>
        
        {/* Search */}
        <div className="relative w-full md:w-64">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
          <input
            type="text"
            placeholder="Search customers..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPagination(p => ({...p, page: 1})); }}
            data-testid="customers-search"
            className="w-full pl-10 pr-4 py-2 border border-border-default focus:border-primary focus:outline-none font-body text-sm"
          />
        </div>
      </div>

      {/* Customers Table */}
      <div className="admin-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border-default bg-background-subtle">
                <th className="text-left font-body text-xs uppercase tracking-wider text-text-secondary py-4 px-4">Customer</th>
                <th className="text-left font-body text-xs uppercase tracking-wider text-text-secondary py-4 px-4">Contact</th>
                <th className="text-left font-body text-xs uppercase tracking-wider text-text-secondary py-4 px-4">City</th>
                <th className="text-center font-body text-xs uppercase tracking-wider text-text-secondary py-4 px-4">Invoices</th>
                <th className="text-right font-body text-xs uppercase tracking-wider text-text-secondary py-4 px-4">Total Purchases</th>
                <th className="text-right font-body text-xs uppercase tracking-wider text-text-secondary py-4 px-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i} className="border-b border-border-default">
                    <td colSpan={6} className="py-4 px-4">
                      <div className="h-12 bg-gray-100 animate-pulse rounded" />
                    </td>
                  </tr>
                ))
              ) : customers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-12 text-center">
                    <p className="font-body text-text-secondary">No customers found</p>
                  </td>
                </tr>
              ) : (
                customers.map((customer) => (
                  <motion.tr
                    key={customer.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="border-b border-border-default hover:bg-background-subtle transition-colors"
                    data-testid={`customer-row-${customer.id}`}
                  >
                    <td className="py-4 px-4">
                      <span className="font-body text-sm text-secondary font-medium">
                        {customer.name}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <div>
                        {customer.phone && (
                          <p className="font-body text-sm text-secondary">{customer.phone}</p>
                        )}
                        {customer.email && (
                          <p className="font-body text-xs text-text-muted">{customer.email}</p>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <span className="font-body text-sm text-text-secondary">
                        {customer.city || '-'}
                      </span>
                    </td>
                    <td className="py-4 px-4 text-center">
                      <span className="font-body text-sm text-secondary">
                        {customer.total_invoices || 0}
                      </span>
                    </td>
                    <td className="py-4 px-4 text-right">
                      <div>
                        <p className="font-body text-sm text-secondary">
                          {formatPrice(customer.total_purchases || 0)}
                        </p>
                        {customer.total_balance > 0 && (
                          <p className="font-body text-xs text-red-500">
                            Due: {formatPrice(customer.total_balance)}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center justify-end">
                        <button
                          onClick={() => handleViewCustomer(customer.id)}
                          data-testid={`view-customer-${customer.id}`}
                          className="p-2 text-text-muted hover:text-primary transition-colors"
                        >
                          <Eye size={18} />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex justify-center gap-2 p-4 border-t border-border-default">
            {[...Array(Math.min(pagination.totalPages, 5))].map((_, i) => (
              <button
                key={i}
                onClick={() => setPagination(p => ({ ...p, page: i + 1 }))}
                className={`w-8 h-8 font-body text-sm transition-colors ${
                  pagination.page === i + 1 
                    ? 'bg-primary text-white' 
                    : 'bg-background-subtle text-secondary hover:bg-primary/10'
                }`}
              >
                {i + 1}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Customer Detail Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between p-4 border-b border-border-default sticky top-0 bg-white">
              <h3 className="font-display text-xl text-secondary">Customer Details</h3>
              <button onClick={() => { setShowModal(false); setSelectedCustomer(null); }}>
                <X size={24} className="text-text-muted hover:text-secondary" />
              </button>
            </div>
            
            {loadingCustomer ? (
              <div className="p-6 space-y-4">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="h-8 bg-gray-100 animate-pulse rounded" />
                ))}
              </div>
            ) : selectedCustomer && (
              <div className="p-6 space-y-6">
                {/* Customer Info */}
                <div>
                  <h4 className="font-display text-2xl text-secondary mb-2">
                    {selectedCustomer.customer?.name}
                  </h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-body text-text-muted">Phone:</span>
                      <span className="font-body text-secondary ml-2">
                        {selectedCustomer.customer?.phone || '-'}
                      </span>
                    </div>
                    <div>
                      <span className="font-body text-text-muted">Email:</span>
                      <span className="font-body text-secondary ml-2">
                        {selectedCustomer.customer?.email || '-'}
                      </span>
                    </div>
                    <div>
                      <span className="font-body text-text-muted">City:</span>
                      <span className="font-body text-secondary ml-2">
                        {selectedCustomer.customer?.city || '-'}
                      </span>
                    </div>
                    <div>
                      <span className="font-body text-text-muted">Pincode:</span>
                      <span className="font-body text-secondary ml-2">
                        {selectedCustomer.customer?.pincode || '-'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-4 bg-background-subtle text-center">
                    <p className="font-body text-xs text-text-muted uppercase mb-1">Total Orders</p>
                    <p className="font-display text-2xl text-secondary">
                      {selectedCustomer.customer?.total_invoices || 0}
                    </p>
                  </div>
                  <div className="p-4 bg-background-subtle text-center">
                    <p className="font-body text-xs text-text-muted uppercase mb-1">Total Spent</p>
                    <p className="font-display text-xl text-secondary">
                      {formatPrice(selectedCustomer.customer?.total_purchases || 0)}
                    </p>
                  </div>
                  <div className="p-4 bg-background-subtle text-center">
                    <p className="font-body text-xs text-text-muted uppercase mb-1">Balance</p>
                    <p className={`font-display text-xl ${selectedCustomer.customer?.total_balance > 0 ? 'text-red-500' : 'text-green-600'}`}>
                      {formatPrice(selectedCustomer.customer?.total_balance || 0)}
                    </p>
                  </div>
                </div>

                {/* Recent Invoices */}
                {selectedCustomer.invoices?.length > 0 && (
                  <div>
                    <h5 className="font-body text-xs uppercase text-text-muted mb-3">Recent Invoices</h5>
                    <div className="space-y-2">
                      {selectedCustomer.invoices.map((invoice) => (
                        <div key={invoice.id} className="flex justify-between items-center p-3 bg-background-subtle">
                          <div>
                            <p className="font-body text-sm text-secondary">{invoice.invoice_number}</p>
                            <p className="font-body text-xs text-text-muted">{formatDate(invoice.invoice_date)}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-body text-sm text-secondary">{formatPrice(invoice.total_amount)}</p>
                            <p className={`font-body text-xs capitalize ${
                              invoice.status === 'paid' ? 'text-green-600' : 
                              invoice.status === 'pending' ? 'text-yellow-600' : 'text-red-500'
                            }`}>
                              {invoice.status}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AdminCustomers;
